import { useId, useState } from 'react'
import './project-cards.css'

const frontCategories: Record<string, string> = { Kaeru: 'Brand strategy', 'Pierce Design': 'Brand & web', 'Anointed Honey': 'Brand', Cardshow: 'Product' }

const base = `${import.meta.env.BASE_URL}site-direction/`
const selected = [
  {
    "name": "Kaeru",
    "pattern": "circles",
    "type": "Brand & positioning",
    "image": "kaeru.jpg",
    "overlay": "kaeru-selected-logo.png",
    "style": "photo",
    "alt": "Forest shrine imagery for Kaeru",
    "description": "A wellness brand built around the ritual of slowing down and taking care.",
    "details": [
      "Kaeru began with CBD balms, lotions and tinctures in a crowded category where the conversation often came down to ingredients and potency. Discovery work uncovered a more personal thread: the founders cared about the ritual of self-care.",
      "The brand story shifted toward slowing down, applying products with intention and creating a practice people could return to. Positioning, messaging and visual direction brought that idea together, presenting the products as tools for a considered wellness ritual. The work started with what the founders valued and turned it into a clearer way to introduce the brand."
    ],
    "scope": [
      "Founder discovery & brand strategy",
      "Ritual-led positioning & messaging",
      "Calm, nature-led visual direction"
    ]
  },
  {
    "name": "Pierce Design",
    "pattern": "blueprint",
    "type": "Brand & web",
    "image": "pierce.png",
    "style": "photo",
    "alt": "Pierce Design landscape and brand illustration",
    "description": "Bringing a design-build business’s craft and story into its digital presence.",
    "details": [
      "Pierce brought years of craftsmanship to the work, but a basic website and word-of-mouth referrals made it hard for prospective clients to see that quality before a first conversation.",
      "The project connected brand creation with a clearer digital experience: a visual identity built around precision, a portfolio that shows the design sensibility behind the construction, and an onboarding approach for qualifying leads and scheduling consultations. The aim was to give the craft a stronger presence while making room for the business owner to stay focused on the job site."
    ],
    "scope": [
      "Brand positioning & visual identity",
      "Design-build portfolio & web presence",
      "Client intake & onboarding"
    ]
  },
  {
    "name": "Anointed Honey",
    "pattern": "honeycomb",
    "type": "Brand & community",
    "image": "anointed-honey-transparent.png",
    "banner": "anointed-honey-banner.jpg",
    "style": "honey",
    "alt": "Anointed Honey gold bee emblem on a transparent background",
    "description": "Raw local honey, hive hosting and colony care in San José.",
    "details": [
      "The story started with a local beekeeper in San José’s East Foothills who wanted to share more than the honey harvest. Bees, pollination and the relationship between a healthy hive and its surroundings gave the brand a wider purpose.",
      "Brand work connected the craft of beekeeping with place, local food and community education. The earlier SJ Hunny strand explored workshops, school visits, farmers markets and packaging that could tell the story of a harvest. Anointed Honey carries the local focus through raw honey, hive hosting and colony care—a way to introduce people to the life around their hives."
    ],
    "scope": [
      "Local brand story & identity",
      "Bee education & community content",
      "Harvest storytelling & packaging direction"
    ]
  },
  {
    "name": "Cardshow",
    "pattern": "cards",
    "type": "Product & creative tools",
    "image": "cardshow.png",
    "banner": "cardshow-roster-banner.jpg",
    "style": "cardshow",
    "alt": "Cardshow",
    "description": "Exploring a more expressive way to present art and digital collectibles.",
    "details": [
      "Cardshow grew from a personal interest in collecting into an independent creative product for collectors, creators and the communities around them. The starting point is simple: turn something you care about into a card worth keeping and sharing.",
      "The exploration brings together interactive card presentation, 3D viewing, lighting and visual effects, using the familiar trading-card format as a canvas for expression. Product thinking and visual experimentation meet in the details of how a card is made, displayed and experienced—giving personal photographs, artwork and collections a more playful home."
    ],
    "scope": [
      "Product concept & creative direction",
      "Interactive cards & 3D presentation",
      "Lighting, motion & visual effects"
    ]
  }
]
export const projectLinks: Record<string, string> = { "Anointed Honey": "https://www.instagram.com/Anointed_honey", Cardshow: "https://cardshow.app", Vaya: "https://vaya.me" }

const homeDescriptions: Record<string, string> = {
  "Pierce Design": "Helping a design-build business bring its craft and story into its digital presence.",
  "Kaeru": "Shaping a wellness brand around everyday rituals, with a clearer story and a more considered identity.",
  "Anointed Honey": "Raw local honey, hive hosting and colony care in San José. A brand rooted in bees, place and the community around its hives.",
  "Cardshow": "An ongoing creative project exploring how people turn the things they care about into collectible cards."
}

export default function ProjectCards({ context = 'portfolio' }: { context?: 'home' | 'portfolio' }) {
  const idPrefix = useId()
  const [flippedProjects, setFlippedProjects] = useState<string[]>([])
  const flipProject = (name: string, card: HTMLElement | null, showDetails: boolean) => {
    setFlippedProjects(current => showDetails ? [...current.filter(project => project !== name), name] : current.filter(project => project !== name))
    requestAnimationFrame(() => card?.querySelector<HTMLButtonElement>(showDetails ? '.work-project-back button' : '.work-project-front button')?.focus({ preventScroll: true }))
  }
  return <div className="work-project-grid dugout-project-cards">{selected.map((project, index) => {
        const flipped = flippedProjects.includes(project.name)
        return <article key={project.name} className={`work-project pattern-${project.pattern}${flipped ? ' is-flipped' : ''}`} onClick={event => {
          if (!(event.target as HTMLElement).closest('a, button')) flipProject(project.name, event.currentTarget, !flipped)
        }}>
          <div className="work-project-inner">
            <div className="work-project-face work-project-front" aria-hidden={flipped}>
              <div className={`work-project-image work-${project.style}${project.name === 'Pierce Design' ? ' work-pierce' : ''}${project.name === 'Kaeru' ? ' work-kaeru-scene' : ''}`}>
                {project.banner && <img className="work-project-banner" src={`${base}assets/${project.banner}`} alt={project.style === 'honey' ? 'Honey bees on a golden wax honeycomb' : 'A roster of illustrated baseball players in different uniforms from Cardshow'} loading="lazy" />}
                <img className={project.banner ? 'work-project-brand' : undefined} src={`${base}assets/${project.image}`} alt={project.alt} loading="lazy" />
                {project.overlay && <img className="work-kaeru-logo" src={`${base}assets/${project.overlay}`} alt="Kaeru gold frog emblem and wordmark" width="269" height="275" loading="lazy" />}
                {project.name === 'Pierce Design' && <svg className="work-pierce-brand" viewBox="162 334 264 92" role="img" aria-label="Pierce" focusable="false"><image href={`${base}assets/pierce.png`} width="587" height="454" /></svg>}
              </div>
              <div className="work-project-title"><h3 className="ds-type-card">{project.name}</h3><p className="work-project-category ds-type-label">{frontCategories[project.name]}</p></div>
              <p className="work-project-description ds-type-supporting">{context === 'home' ? homeDescriptions[project.name] : project.description}</p>
              <div className="work-project-control-row"><button type="button" className="work-project-flip" tabIndex={flipped ? -1 : 0} aria-label={`Show ${project.name} project details`} aria-expanded={flipped} aria-controls={`${idPrefix}-project-details-${index}`} onClick={event => flipProject(project.name, event.currentTarget.closest('article'), true)}>Details</button></div>
            </div>
            <div id={`${idPrefix}-project-details-${index}`} className="work-project-face work-project-back" aria-hidden={!flipped}>
              <p className="work-project-category ds-type-label">{project.type}</p>
              <div className="work-project-title"><h3 className="ds-type-card">{project.name}</h3>{projectLinks[project.name] && <a className="work-project-visit" tabIndex={flipped ? 0 : -1} href={projectLinks[project.name]} target="_blank" rel="noopener noreferrer">Visit project<span className="work-sr-only"> (opens in a new tab: {project.name})</span></a>}</div>
              <div className="work-project-story">{project.details.map(paragraph => <p key={paragraph} className="ds-type-body">{paragraph}</p>)}</div>
              <section className="work-project-scope" aria-label={`${project.name} project focus`}>
                <p className="work-kicker ds-type-label">PROJECT FOCUS</p>
                <ul>{project.scope.map(item => <li key={item}>{item}</li>)}</ul>
                <div className="work-project-control-row"><button type="button" className="work-project-flip work-project-return" tabIndex={flipped ? 0 : -1} aria-label={`Show ${project.name} overview`} onClick={event => flipProject(project.name, event.currentTarget.closest('article'), false)}>Back to overview</button></div>
              </section>
            </div>
          </div>
        </article>
      })}</div>
}
